// dev-0.7.51 — Shared "Use This Configuration" restore logic.
//
// Both the Dashboard and the Execute page open AllCompletedRunsModal and wire
// its "Use This Configuration" button to an onActivityClick handler. Each used
// to build the restored config independently (Dashboard cherry-picked a subset
// of fields; Execute was a no-op stub), so the two surfaces drifted and most
// datapoints — sites[], selected_datasets, feature_filters, imaging/
// segmentation hyperparameters — were silently dropped.
//
// This module centralises the rebuild so every entry point restores EVERY
// datapoint the run was launched with. When the run carries a stored
// config_data blob (the case for everything launched through the configs page
// since config_data shipped) it is passed through wholesale, which also means
// any future field is restored automatically. A flat-column reconstruction is
// kept only as a fallback for legacy runs that predate config_data.

import { authenticatedFetch } from './api';
import { API_BASE } from '../config/environment';

/**
 * Build a complete configData object from a past-run activity, ready to feed
 * into saveConfig()/setCurrentConfig(). Returns null when the activity has no
 * usable execution data.
 *
 * @param {object} activity - activity row from AllCompletedRunsModal
 * @returns {Promise<object|null>}
 */
export async function buildConfigDataFromActivity(activity) {
  if (!activity || activity.type !== 'execution' || !activity.executionData) {
    return null;
  }

  const executionData = activity.executionData;

  // Prefer the complete backend record (it carries config_data); fall back to
  // whatever the activity row already has if the fetch fails.
  let fullTrainingData = executionData;
  if (executionData.trainingRunId) {
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/user/training-runs/${executionData.trainingRunId}`
      );
      if (res && res.ok) {
        const data = await res.json();
        const trainingRun = data?.training_run || (data?.success ? data.training_run : null);
        if (trainingRun) {
          fullTrainingData = { ...executionData, ...trainingRun };
        }
      }
    } catch (err) {
      console.warn('[restoreRunConfig] Could not fetch full training data, using row data:', err?.message || err);
    }
  }

  // Parse the stored config_data blob (string or object).
  let storedConfig = {};
  if (fullTrainingData.config_data) {
    try {
      storedConfig =
        typeof fullTrainingData.config_data === 'string'
          ? JSON.parse(fullTrainingData.config_data)
          : fullTrainingData.config_data;
    } catch (e) {
      console.warn('[restoreRunConfig] Failed to parse config_data:', e);
    }
  }

  const hasStoredBlob =
    storedConfig &&
    typeof storedConfig === 'object' &&
    (storedConfig.app_config ||
      storedConfig.dataset_config ||
      Array.isArray(storedConfig.sites));

  // ── Primary path: pass the stored blob through wholesale ──────────────────
  if (hasStoredBlob) {
    const configData = JSON.parse(JSON.stringify(storedConfig));
    // Strip persistence-only metadata that may ride along on the blob.
    delete configData.savedAt;
    delete configData.version;
    // Normalize model_type casing — the ClientApp dispatcher is case-sensitive.
    if (configData.dataset_config && configData.dataset_config.model_type) {
      configData.dataset_config.model_type = String(
        configData.dataset_config.model_type
      ).toLowerCase();
    }
    return configData;
  }

  // ── Legacy fallback: reconstruct from flat columns ───────────────────────
  const getValue = (key, flatKey, defaultVal) => {
    if (storedConfig.app_config && storedConfig.app_config[key] !== undefined) return storedConfig.app_config[key];
    if (storedConfig[key] !== undefined) return storedConfig[key];
    if (fullTrainingData[flatKey] !== undefined) return fullTrainingData[flatKey];
    if (fullTrainingData[key] !== undefined) return fullTrainingData[key];
    return defaultVal;
  };

  const storedModelType = storedConfig?.dataset_config?.model_type || storedConfig.model_type;
  const actualModelType = fullTrainingData.model_type || fullTrainingData.modelType;
  const modelType = String(storedModelType || actualModelType || 'logreg').toLowerCase();

  const datasetPath =
    storedConfig?.dataset_config?.dataset_path ||
    storedConfig.dataset_path ||
    fullTrainingData.dataset_path ||
    fullTrainingData.datasetPath ||
    '';

  // Map model → canonical processing_type token (must match the tokens the
  // configs page validates: 'imaging_classification' / 'imaging_segmentation').
  let processingType = storedConfig?.dataset_config?.processing_type || '';
  if (!processingType || processingType === 'imaging' || processingType === 'segmentation') {
    if (['convnext', 'convnext_3d', 'resnet18', 'swintransformer'].includes(modelType)) processingType = 'imaging_classification';
    else if (modelType === 'unet') processingType = 'imaging_segmentation';
    else processingType = 'tabular';
  }

  let selectedFeatures = [];
  if (storedConfig.tabular_config?.selected_features) {
    selectedFeatures = storedConfig.tabular_config.selected_features;
  } else if (fullTrainingData.tabular_config?.selected_features) {
    selectedFeatures = fullTrainingData.tabular_config.selected_features;
  }

  const legacySites = Array.isArray(storedConfig.sites)
    ? storedConfig.sites
    : (Array.isArray(fullTrainingData.sites) ? fullTrainingData.sites : null);
  const legacySelectedDatasets =
    storedConfig?.dataset_config?.selected_datasets ||
    fullTrainingData?.selected_datasets ||
    (datasetPath ? [datasetPath] : []);

  const configData = {
    app_config: {
      'num-server-rounds': getValue('num-server-rounds', 'num_server_rounds', 3),
      'fraction-evaluate': getValue('fraction-evaluate', 'fraction_evaluate', 1.0),
      'local-epochs': getValue('local-epochs', 'local_epochs', 2),
      'learning-rate': getValue('learning-rate', 'learning_rate', 0.001),
      'batch-size': getValue('batch-size', 'batch_size', 32),
    },
    dataset_config: {
      model_type: modelType,
      dataset_path: datasetPath,
      selected_datasets: legacySelectedDatasets,
      processing_type: processingType,
    },
    ...(legacySites ? { sites: legacySites } : {}),
    ...(processingType === 'tabular' && {
      tabular_config: {
        selected_features: selectedFeatures,
        feature_filters: storedConfig?.tabular_config?.feature_filters || {},
      },
    }),
    ...(processingType === 'imaging_classification' && {
      imaging_config: {
        partitioning_method: getValue('partitioning_method', 'partitioning_method', 'iid'),
        l1_lambda: getValue('l1_lambda', 'l1_lambda', 0.0),
        l2_lambda: getValue('l2_lambda', 'l2_lambda', 0.0),
        regularization_norm: getValue('regularization_norm', 'regularization_norm', 2),
        input_channels: getValue('input_channels', 'input_channels', 1),
        num_classes: getValue('num_classes', 'num_classes', 2),
      },
    }),
    ...(processingType === 'imaging_segmentation' && {
      segmentation_config: {
        in_channels: getValue('in_channels', 'input_channels', 1),
        n_classes: getValue('n_classes', 'num_classes', 1),
        partitioning_method: getValue('partitioning_method', 'partitioning_method', 'iid'),
      },
    }),
  };

  return configData;
}
